/* eslint-disable react/jsx-props-no-spreading */
import { Box, Button, Collapse, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';

import { copy, paste } from '../../api/ventura/lineItem';
import { API_CONTRACT_SEARCH_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { useSnackBar } from '../../hooks/useSnackBar';
import mapFormToLineItemUtil from '../../util/mapFormToLineItem';
import mapLineItemToFormUtil from '../../util/mapLineItemToForm';
import posthog from '../../util/posthog';
import { buildKpiGoalWithoutReceipts, hasReceiptsKpi } from '../../util/receiptKpi';
import { getRequestJSON } from '../../util/services';
import FormEndOptions from '../forms/FormEndOptions';
import TypeaheadTextField from '../forms/TypeaheadTextField';
import DuplicateLineItemFields from './DuplicateLineItemFields';

interface DuplicateLineItemFormProps {
  lineItem: any;
  onClose?: Function;
}

const mapLineItemToForm = (response) => {
  const form = mapLineItemToFormUtil(response);
  return {
    ...response,
    advisory_budget: form.advisoryBudget,
    bonus: form.bonus,
    campaigns: Object.entries(response.campaigns).map(([key, value]) => ({
      ...(value as object),
      adGroupId: key,
    })),
    customDeliveryProfiles: form.customDeliveryProfiles || [],
    // ADOPS-2722 — deselect toggles for risky settings. Default to true so current
    // behavior (backend copies these from the source) is preserved unless the user
    // opts out; see mapFormToLineItem for how an opt-out becomes a payload override.
    copyFrequencyCaps: true,
    copyDailyImpressionLimit: true,
    copyDynamicSpendPacing: true,
    // ADOPS-2706 — Receipts KPI (goal + product/category selections) is carried by
    // default; deselecting sends the overrides that drop it from the clone.
    copyReceiptKpi: true,
  };
};

const mapFormToLineItem = (form, sourceLineItem?: any) => {
  const campaigns = {};
  form.new_insertion_order.campaigns.forEach((item) => {
    campaigns[item.adGroupId] = {
      ...item,
      adGroupId: undefined,
    };
  });
  const newLineItem = mapFormToLineItemUtil(form.new_insertion_order);
  const result: any = {
    ...form,
    new_insertion_order: {
      ...form.new_insertion_order,
      // We want the UI's end date to be saved on the backend's final_end_date.
      end_date: undefined,
      final_end_date: form.new_insertion_order.end_date,
      advisory_budget: newLineItem.advisory_budget,
      bonus: Number(newLineItem.bonus),
      campaigns,
    },
  };
  if (form.new_insertion_order.copyDeliveryProfiles && newLineItem.delivery_profiles?.length > 0) {
    result.new_insertion_order.clone_delivery_profiles = true;
    result.new_insertion_order.delivery_profiles = newLineItem.delivery_profiles;
  }

  // ADOPS-2722 — When a risky setting is deselected, override it in the payload so the
  // clone starts clean. When left selected we send nothing extra, so the backend copies
  // the original value as before (the slim copy response omits these fields).
  if (!form.new_insertion_order.copyFrequencyCaps) {
    result.new_insertion_order.frequency_capping = null;
    result.new_insertion_order.max_user_views_per_period = null;
    result.new_insertion_order.click_frequency_capping = null;
    result.new_insertion_order.max_user_clicks_per_period = null;
  }
  if (!form.new_insertion_order.copyDailyImpressionLimit) {
    result.new_insertion_order.daily_impression_limit = null;
  }
  if (!form.new_insertion_order.copyDynamicSpendPacing) {
    result.new_insertion_order.dynamic_spend_pacing = false;
    result.new_insertion_order.pacing_strategies = [];
  }

  // ADOPS-2706 — the Receipts KPI goal rides along on the line item's kpi_goal column, so
  // the backend copies it whatever we do; its selections live in receipt_product_list,
  // which the backend clones unless told not to. Only send overrides on an opt-out: skip
  // the selections and rewrite kpi_goal without Receipts, so the clone is not left with a
  // Receipts goal that has nothing selected.
  if (hasReceiptsKpi(sourceLineItem) && !form.new_insertion_order.copyReceiptKpi) {
    result.new_insertion_order.clone_receipt_product_list = false;
    const kpiGoal = buildKpiGoalWithoutReceipts(sourceLineItem?.kpiGoal);
    if (kpiGoal) {
      result.new_insertion_order.kpi_goal = kpiGoal;
    }
  }

  // Clean up form-only fields
  delete result.new_insertion_order.customDeliveryProfiles;
  delete result.new_insertion_order.copyDeliveryProfiles;
  delete result.new_insertion_order.copyFrequencyCaps;
  delete result.new_insertion_order.copyDailyImpressionLimit;
  delete result.new_insertion_order.copyDynamicSpendPacing;
  delete result.new_insertion_order.copyReceiptKpi;
  delete result.new_insertion_order.deletedProfileIds;
  return result;
};

function CloneLineItemForm({ lineItem, onClose }: DuplicateLineItemFormProps) {
  const { lineItemId } = lineItem;
  const { apiHost, apiHostFor } = useConfig();
  const { showSnackBar } = useSnackBar();
  const [cloneDifferentParent, setCloneDifferentParent] = useState(false);
  const defaultValues = {
    id: null,
    number_of_copies: 1,
    new_insertion_order: {
      advisory_budget: null,
      bid_priority: null,
      bonus: null,
      start_date: null,
      end_date: null,
      campaigns: null,
      impression_budget: null,
      contract_id: null,
      customDeliveryProfiles: [],
      copyDeliveryProfiles: true,
      copyFrequencyCaps: true,
      copyDailyImpressionLimit: true,
      copyDynamicSpendPacing: true,
      copyReceiptKpi: true,
    },
  };

  const form = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const { handleSubmit, watch, reset, trigger, formState } = form;
  // Subscribe to validity/errors so the Duplicate button stays disabled while the form
  // is invalid or carries any field errors. In 'onBlur' mode isValid only updates after a
  // validation pass, so we trigger() once the seed data loads (see effect below) to keep it
  // accurate before the user touches anything.
  const { isValid, errors } = formState;
  const isDuplicateDisabled = !isValid || Object.keys(errors).length > 0;

  const pasteMutation = useMutation({
    mutationFn: (payload) => paste(apiHost, lineItemId, payload),
    onSuccess: (response) => {
      posthog.capture('line_item_duplicated', { source_line_item_id: lineItemId });
      showSnackBar(response.status, 'success', {
        event: 'line_item_duplicated',
        source_line_item_id: lineItemId,
      });
      if (onClose) {
        onClose();
      }
    },
  });

  const [selectedContractId] = watch(['new_insertion_order.contract_id']);

  const { data } = useQuery({
    queryKey: ['copyLineItem', { lineItemId, selectedContractId }],
    queryFn: () => copy(apiHost, lineItemId, selectedContractId),
    select: mapLineItemToForm,
  });

  useEffect(() => {
    if (data) {
      reset({
        id: lineItemId,
        number_of_copies: defaultValues.number_of_copies,
        new_insertion_order: data,
      });
      // Compute validity for the freshly seeded values so the Duplicate button reflects the
      // real form state immediately, instead of staying disabled until the user blurs a field.
      trigger();
    }
  }, [reset, trigger, data, lineItemId, defaultValues.number_of_copies]);

  const submitHandler = (payload) => {
    pasteMutation.mutate(mapFormToLineItem(payload, lineItem));
  };

  const getContractOptions = async (term) => {
    // Contract-name autocomplete opts into the per-domain flip
    // (apiHostFor('contracts')): the route is ACH-served and 'contracts' is
    // flipped. This form passes only term + insertion_order_id (no contract_id).
    // ACH and Rails both return a BARE {id,label,...} array (no envelope), and
    // this reads only item.label + item.id, so no adaptation. apiHost stays for
    // the copy/paste verbs, which are not ACH-served.
    const response = await getRequestJSON(
      apiHostFor('contracts'),
      generatePath(API_CONTRACT_SEARCH_URL, {
        '*': `?${queryString.stringify({
          term,
          insertion_order_id: lineItemId || '',
        })}`,
      })
    );
    return response.map((item) => ({ label: item.label, value: item.id }));
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Stack pb={2}>
          <Typography variant="h5" mx={2} fontWeight="bold" gutterBottom>
            Duplicate {lineItem.lineItemName}
          </Typography>
          <DuplicateLineItemFields lineItem={lineItem} />
          <FormControlLabel
            label="Duplicate to another Parent Item"
            control={<Switch onClick={() => setCloneDifferentParent(!cloneDifferentParent)} />}
          />
          <Collapse in={cloneDifferentParent} timeout="auto" unmountOnExit>
            <Box sx={{ height: '200px' }}>
              <Stack alignItems="flex-start" spacing={2} py={2}>
                <TypeaheadTextField
                  label="Contract"
                  name="new_insertion_order.contract_id"
                  rules={{ required: { value: true, message: 'Contract is required' } }}
                  fullWidth
                  getOptions={getContractOptions}
                />
              </Stack>
            </Box>
          </Collapse>
        </Stack>
        <FormEndOptions>
          <Stack direction="row" justifyContent="center" width="100%" spacing={2}>
            <Button variant="outlined" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button type="submit" disabled={isDuplicateDisabled}>
              Duplicate
            </Button>
          </Stack>
        </FormEndOptions>
      </form>
    </FormProvider>
  );
}

export default CloneLineItemForm;
